import { NavLink } from 'react-router-dom'

import { BrandLockup } from '@/components/layout/BrandLockup'
import { Button } from '@/components/ui/Button'
import { ChevronDownIcon, UserPlusIcon } from '@/components/ui/icons'
import { DEMO_BADGES, NAV_ITEMS } from '@/app/navigation'
import { cn } from '@/lib/utils/cn'
import { env } from '@/lib/env'
import { useAuth } from '@/features/auth/authContext'
import { useTerm, useTermLabel } from '@/hooks/useTerm'
import { useUiStore } from '@/stores/uiStore'
import { DEFAULT_DISPLAY_NAME } from '@/types/workspace'

import styles from './WorkspaceNav.module.css'

function initials(value: string): string {
  return value
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
}

export function WorkspaceNav() {
  const { role, location, workspace } = useAuth()
  const displayName = workspace?.displayName ?? DEFAULT_DISPLAY_NAME
  const openLeadIntake = useUiStore((state) => state.openLeadIntake)
  const badges = env.demoMode && !env.sharedShow ? DEMO_BADGES : { calls: 0, notifications: 0 }
  const termLabel = useTermLabel()
  const customer = useTerm('customer')

  return (
    <header className={styles.nav}>
      <BrandLockup
        className={styles.brand}
        variant="inverse"
        mark="dots"
        tenant={displayName}
        logoUrl={workspace?.logoUrl}
      />

      <nav className={styles.items} aria-label="Hauptnavigation">
        {NAV_ITEMS.map(({ to, label, Icon, badge }) => {
          const count = badge ? badges[badge] : 0

          return (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => cn(styles.item, isActive && styles.active)}
            >
              <Icon />
              {termLabel(label)}
              {count > 0 && <span className={styles.count}>{count}</span>}
            </NavLink>
          )
        })}
      </nav>

      <Button
        className={styles.intake}
        variant="secondary"
        size="sm"
        leadingIcon={<UserPlusIcon />}
        onClick={openLeadIntake}
      >
        {`${customer.singular} erstellen`}
      </Button>

      <div className={styles.tenant}>
        <span className={styles.tenantAvatar} aria-hidden="true">
          {initials(displayName)}
        </span>
        <span className={styles.tenantMeta}>
          <strong>{displayName}</strong>
          <span>{role ? `${role.label} · ${location?.name ?? ''}` : ''}</span>
        </span>
        <ChevronDownIcon className={styles.tenantChevron} />
      </div>
    </header>
  )
}
